import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import {CommonModule} from '@angular/common';

@Component({
  selector: 'app-sms-code-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sms-code-input.component.html',
  styleUrls: ['./sms-code-input.component.scss']
})
export class SmsCodeInputComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() length = 6;
  @Input() disabled = false;
  @Output() codeChange = new EventEmitter<string>();

  @ViewChildren('codeInput') codeInputs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  private syncTimer?: number;

  ngOnInit(): void {
    this.digits = Array.from({length: this.length}, () => '');
  }

  ngAfterViewInit(): void {
    // Browser restore or OTP autofill can populate DOM inputs without updating component state.
    queueMicrotask(() => this.syncFromInputs());
    this.syncTimer = window.setInterval(() => this.syncFromInputs(), 250);
  }

  ngOnDestroy(): void {
    if (this.syncTimer) {
      clearInterval(this.syncTimer);
    }
  }

  onInput(event: Event, index: number) {
    const value = this.updateDigitFromEvent(event, index);
    this.emitCode();

    if (value && index < this.length - 1) {
      this.focusIndex(index + 1);
    }
  }

  onChange(event: Event, index: number) {
    this.updateDigitFromEvent(event, index);
    this.emitCode();
  }

  onKeyUp() {
    this.syncFromInputs();
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
      this.focusIndex(index - 1);
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text')?.replace(/\D/g, '') ?? '';
    if (!pasted) return;

    const values = pasted.slice(0, this.length).split('');
    values.forEach((digit, idx) => {
      this.digits[idx] = digit;
    });

    this.syncInputs();
    this.emitCode();
  }

  private updateDigitFromEvent(event: Event, index: number): string {
    const target = event.target as HTMLInputElement;
    const value = target.value.replace(/\D/g, '');
    this.digits[index] = value.slice(-1);
    target.value = this.digits[index];
    return this.digits[index];
  }

  private emitCode() {
    this.codeChange.emit(this.digits.join(''));
  }

  private focusIndex(index: number) {
    const inputs = this.codeInputs?.toArray() ?? [];
    inputs[index]?.nativeElement?.focus();
  }

  private syncInputs() {
    const inputs = this.codeInputs?.toArray() ?? [];
    inputs.forEach((ref, idx) => {
      ref.nativeElement.value = this.digits[idx] ?? '';
    });
  }

  private syncFromInputs() {
    const inputs = this.codeInputs?.toArray() ?? [];
    if (!inputs.length) {
      return;
    }

    let changed = false;
    inputs.forEach((ref, idx) => {
      const digit = ref.nativeElement.value.replace(/\D/g, '').slice(-1);
      if ((this.digits[idx] ?? '') !== digit) {
        this.digits[idx] = digit;
        changed = true;
      }
    });

    if (changed) {
      this.emitCode();
    }
  }
}
